import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  User,
  Briefcase,
  Award,
  Clock,
  Sparkles,
  FolderOpen,
  CheckCircle2,
  Loader,
  Save,
  Plus,
  X,
  Sparkle,
  Trophy,
  Upload,
  Trash2,
  Cpu,
  Link2,
} from 'lucide-react';
import LinkPreviewCard from '../components/LinkPreviewCard';
import { fetchLinkPreview } from '../services/linkPreview';
import { useAuth } from '../contexts/AuthContext';
import {
  TaskerMetaForm,
  ExperienceLevel,
  AvailabilityPreference,
  WorkLocationPreference,
} from '../types';
import {
  saveTaskerProfile,
  getTaskerProfileByUserId,
} from '../api/taskerProfiles';
import { useNavigate } from 'react-router-dom';
import { uploadProjectImage } from '../api/storage';

// AI-native profile flow: identity → story → proof → differentiation → skills → work style
const SECTIONS = [
  {
    id: 'personal',
    title: 'Your identity',
    shortTitle: 'Identity',
    subtitle: 'How you show up on GNK Continuum',
    icon: User,
    color: 'from-cyan-500 to-blue-600',
    prompt: 'Name and a line people remember you by.',
  },
  {
    id: 'ai_showcase',
    title: 'Your AI story',
    shortTitle: 'AI story',
    subtitle: 'Tasks, tools & proof — this is your differentiator vs generic social feeds',
    icon: Cpu,
    color: 'from-emerald-500 to-cyan-500',
    prompt: 'What tasks do you ship? Which AI apps do you actually use — and what have you built?',
  },
  {
    id: 'portfolio',
    title: 'Projects & proof',
    shortTitle: 'Projects',
    subtitle: 'Shipped work, demos, repos',
    icon: FolderOpen,
    color: 'from-violet-500 to-fuchsia-500',
    prompt: 'Link to repos, demos, or write-ups.',
  },
  {
    id: 'specialties',
    title: 'What makes you rare',
    shortTitle: 'Edge',
    subtitle: 'The special sauce — niches, obsessions, combinations',
    icon: Sparkles,
    color: 'from-pink-500 to-rose-500',
    prompt: 'Not corporate titles — your edge in the AI builder economy.',
  },
  {
    id: 'skills',
    title: 'Skills & stack',
    shortTitle: 'Skills',
    subtitle: 'Tags people can match on',
    icon: Award,
    color: 'from-amber-500 to-orange-500',
    prompt: 'Primary skills (e.g. prompting, evals, agents, MLOps).',
  },
  {
    id: 'professional',
    title: 'Headline & links',
    shortTitle: 'Headline',
    subtitle: 'One-liner + GitHub / portfolio URLs',
    icon: Briefcase,
    color: 'from-blue-500 to-cyan-500',
    prompt: 'How you describe your work in one line.',
  },
  {
    id: 'preferences',
    title: 'How you collaborate',
    shortTitle: 'Collab',
    subtitle: 'Availability & working style',
    icon: Clock,
    color: 'from-emerald-500 to-teal-500',
    prompt: 'So the right people can reach you.',
  },
] as const;

const EXPERIENCE_LEVELS: { value: ExperienceLevel; label: string; emoji: string }[] = [
  { value: 'entry', label: 'Getting Started', emoji: '🌱' },
  { value: 'junior', label: 'Junior Level', emoji: '🚀' },
  { value: 'mid', label: 'Mid-Level', emoji: '⭐' },
  { value: 'senior', label: 'Senior', emoji: '🎯' },
  { value: 'expert', label: 'Expert', emoji: '🏆' },
];

const AVAILABILITY_OPTIONS: { value: AvailabilityPreference; label: string; emoji: string }[] = [
  { value: 'full-time', label: 'Full-Time', emoji: '💼' },
  { value: 'part-time', label: 'Part-Time', emoji: '⏰' },
  { value: 'contract', label: 'Contract', emoji: '📋' },
  { value: 'freelance', label: 'Freelance', emoji: '🆓' },
  { value: 'on-demand', label: 'On-Demand', emoji: '⚡' },
];

const LOCATION_OPTIONS: { value: WorkLocationPreference; label: string; emoji: string }[] = [
  { value: 'remote', label: 'Remote Only', emoji: '🌐' },
  { value: 'on-site', label: 'On-Site', emoji: '🏢' },
  { value: 'hybrid', label: 'Hybrid', emoji: '🔄' },
  { value: 'flexible', label: 'Flexible', emoji: '✨' },
];

const TIMEZONES = [
  'UTC',
  'America/Los_Angeles',
  'America/New_York',
  'America/Chicago',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney',
];

const initialFormData: TaskerMetaForm = {
  personalInfo: {
    firstName: '',
    lastName: '',
    displayName: '',
    phone: '',
    location: '',
    timezone: 'UTC',
    bio: '',
  },
  professionalInfo: {
    title: '',
    experienceLevel: 'mid',
    yearsOfExperience: 0,
    currentRole: '',
    company: '',
    linkedInUrl: '',
    portfolioUrl: '',
    githubUrl: '',
  },
  skills: {
    primary: [],
    secondary: [],
    certifications: [],
    languages: [{ code: 'en', name: 'English', proficiency: 'native' }],
  },
  workPreferences: {
    availability: 'freelance',
    location: 'remote',
    preferredHoursPerWeek: 20,
    hourlyRate: 0,
    currency: 'USD',
    minimumTaskBudget: 0,
    timezone: 'UTC',
    schedule: {},
    fastTrackAvailable: false,
  },
  specialties: [],
  services: [],
  portfolio: {
    projects: [],
    achievements: [],
  },
  aiBuilderShowcase: {
    publicTasksStory: '',
    aiToolExperiences: [],
    featuredLinks: [],
  },
};

const TaskerMeta: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<TaskerMetaForm>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [visibleSectionId, setVisibleSectionId] = useState<string>(SECTIONS[0].id);
  const [tempSkill, setTempSkill] = useState('');
  const [skillType, setSkillType] = useState<'primary' | 'secondary'>('primary');
  const [tempSpecialty, setTempSpecialty] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [existingProfile, setExistingProfile] = useState<any>(null);
  const [fetchingLinkIdx, setFetchingLinkIdx] = useState<number | null>(null);

  useEffect(() => {
    if (currentUser) {
      loadExistingProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Debug: Log when formData changes (after profile loads)
  useEffect(() => {
    if (existingProfile && !loading) {
      console.log('📝 FormData state updated:', {
        hasPersonalInfo: !!formData.personalInfo?.firstName,
        firstName: formData.personalInfo?.firstName,
        lastName: formData.personalInfo?.lastName,
        displayName: formData.personalInfo?.displayName,
        hasProfessionalInfo: !!formData.professionalInfo?.title,
        title: formData.professionalInfo?.title,
        skillsCount: formData.skills?.primary?.length || 0,
        specialtiesCount: formData.specialties?.length || 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.personalInfo?.firstName, formData.personalInfo?.lastName, formData.professionalInfo?.title, loading]);

  const loadExistingProfile = async () => {
    if (!currentUser) {
      console.log('No current user, skipping profile load');
      return;
    }
    
    try {
      setLoading(true);
      console.log('Loading profile for userId:', currentUser.uid);
      const profile = await getTaskerProfileByUserId(currentUser.uid);
      console.log('Profile query result:', profile ? 'Found' : 'Not found', profile?.id);
      
      if (profile) {
        setExistingProfile(profile);
        console.log('✅ Loaded existing profile:', {
          id: profile.id,
          completion: profile.completionPercentage,
          hasPersonalInfo: !!profile.personalInfo?.firstName,
          hasProfessionalInfo: !!profile.professionalInfo?.title,
        });
        console.log('📄 Profile personalInfo:', profile.personalInfo);
        console.log('💼 Profile professionalInfo:', profile.professionalInfo);
        console.log('🔍 Checking data values:', {
          firstName: profile.personalInfo?.firstName,
          lastName: profile.personalInfo?.lastName,
          displayName: profile.personalInfo?.displayName,
          title: profile.professionalInfo?.title,
          experienceLevel: profile.professionalInfo?.experienceLevel,
        });
        // Ensure we have valid data before setting form
        const personalInfo = profile.personalInfo || initialFormData.personalInfo;
        const professionalInfo = profile.professionalInfo || initialFormData.professionalInfo;
        const skills = profile.skills || initialFormData.skills;
        const profileWorkPrefs = profile.workPreferences;
        const specialties = profile.specialties || initialFormData.specialties;
        
        console.log('📦 Preparing to set form data:', {
          personalInfoKeys: Object.keys(personalInfo),
          professionalInfoKeys: Object.keys(professionalInfo),
          firstName: personalInfo?.firstName,
          lastName: personalInfo?.lastName,
          title: professionalInfo?.title,
        });
        
        setFormData({
          personalInfo: {
            ...initialFormData.personalInfo,
            ...personalInfo,
          },
          professionalInfo: {
            ...initialFormData.professionalInfo,
            ...professionalInfo,
          },
          skills: {
            ...initialFormData.skills,
            ...skills,
          },
          workPreferences: {
            ...initialFormData.workPreferences,
            availability: profileWorkPrefs?.availability || initialFormData.workPreferences.availability,
            location: profileWorkPrefs?.location || initialFormData.workPreferences.location,
            preferredHoursPerWeek: profileWorkPrefs?.preferredHoursPerWeek || initialFormData.workPreferences.preferredHoursPerWeek,
            hourlyRate: profileWorkPrefs?.hourlyRate || initialFormData.workPreferences.hourlyRate,
            currency: profileWorkPrefs?.currency || initialFormData.workPreferences.currency,
            minimumTaskBudget: profileWorkPrefs?.minimumTaskBudget || initialFormData.workPreferences.minimumTaskBudget,
            fastTrackAvailable: profileWorkPrefs?.fastTrackAvailable ?? initialFormData.workPreferences.fastTrackAvailable,
            schedule: profileWorkPrefs?.availabilitySchedule?.days || {},
            timezone: profileWorkPrefs?.availabilitySchedule?.timezone || 'UTC',
          },
          specialties: specialties || [],
          services: profile.services.map((s) => ({
            title: s.title,
            description: s.description,
            category: s.category,
            hourlyRate: s.hourlyRate,
            fixedRate: s.fixedRate,
            estimatedHours: s.estimatedHours,
          })),
          portfolio: {
            projects: profile.portfolio.projects.map((p) => {
              // Safely convert Firestore Timestamp to Date for completedAt
              let completedAt: Date | null = null;
              const completedAtValue = p.completedAt;
              
              if (completedAtValue) {
                // Check if it's a Firestore Timestamp (has toDate method)
                if (typeof (completedAtValue as any).toDate === 'function') {
                  completedAt = (completedAtValue as any).toDate();
                } else if (completedAtValue instanceof Date) {
                  completedAt = completedAtValue;
                } else {
                  completedAt = new Date(completedAtValue);
                }
                
                // Validate date
                if (completedAt && isNaN(completedAt.getTime())) {
                  completedAt = new Date();
                }
              }
              
              return {
                title: p.title,
                description: p.description,
                category: p.category,
                technologies: p.technologies,
                duration: p.duration,
                role: p.role,
                outcome: p.outcome,
                url: p.url,
                images: p.images,
                completedAt: completedAt || new Date(),
              };
            }),
            achievements: profile.portfolio.achievements.map((a) => {
              // Safely convert Firestore Timestamp to Date
              let date: Date | null = null;
              const dateValue = a.date;
              
              if (dateValue) {
                // Check if it's a Firestore Timestamp (has toDate method)
                if (typeof (dateValue as any).toDate === 'function') {
                  date = (dateValue as any).toDate();
                } else if (dateValue instanceof Date) {
                  date = dateValue;
                } else {
                  date = new Date(dateValue);
                }
                
                // Validate date
                if (date && isNaN(date.getTime())) {
                  date = new Date();
                }
              }
              
              return {
                title: a.title,
                description: a.description,
                category: a.category,
                date: date || new Date(),
                metrics: a.metrics,
                proofUrl: a.proofUrl,
              };
            }),
          },
          aiBuilderShowcase: profile.aiBuilderShowcase
            ? {
                publicTasksStory: profile.aiBuilderShowcase.publicTasksStory || '',
                aiToolExperiences: Array.isArray(profile.aiBuilderShowcase.aiToolExperiences)
                  ? profile.aiBuilderShowcase.aiToolExperiences
                  : [],
                featuredLinks: Array.isArray(profile.aiBuilderShowcase.featuredLinks)
                  ? profile.aiBuilderShowcase.featuredLinks
                  : [],
              }
            : initialFormData.aiBuilderShowcase,
        });
        console.log('✅ Form data updated with profile data');
        // Note: formData state won't update immediately, check in next render
      } else {
        console.log('ℹ️ No existing profile found - user can create new one');
        console.log('This is normal for first-time users');
      }
    } catch (err: any) {
      console.error('❌ Error loading profile:', err);
      console.error('Error details:', {
        code: err?.code,
        message: err?.message,
        userId: currentUser?.uid,
        email: currentUser?.email,
        stack: err?.stack?.substring(0, 200),
      });
      // Don't show error to user if profile just doesn't exist yet
      if (err?.code !== 'permission-denied') {
        // Only log, don't throw - allow user to create new profile
      }
    } finally {
      setLoading(false);
      console.log('Profile loading complete');
    }
  };

  // Auto-save functionality
  const autoSave = useCallback(async () => {
    if (!currentUser || saving) return;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        // Load existing profile first to avoid data loss - if it fails, continue without it
        let currentProfile = existingProfile;
        if (!currentProfile) {
          try {
            currentProfile = await getTaskerProfileByUserId(currentUser.uid);
          } catch (loadErr) {
            // Don't throw - continue without existing profile
            console.warn('Could not load existing profile for auto-save:', loadErr);
            currentProfile = undefined;
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _oldCompletion = currentProfile?.completionPercentage || 0;
        
        await saveTaskerProfile(
          currentUser.uid,
          currentUser.email || '',
          formData,
          currentProfile
        );

        // Update existing profile reference
        const updatedProfile = await getTaskerProfileByUserId(currentUser.uid);
        if (updatedProfile) {
          setExistingProfile(updatedProfile);
          
          // Call cloud function if completion percentage changed significantly
          // This ensures metrics are recalculated server-side
          // Metrics are recalculated locally — no cloud function needed
          
          // Dispatch event to notify menu that profile was updated
          if ((updatedProfile.completionPercentage || 0) >= 50) {
            window.dispatchEvent(new CustomEvent('profileUpdated'));
          }
        }
        
        setLastSaved(new Date());
      } catch (err) {
        console.error('Auto-save failed:', err);
      } finally {
        setSaving(false);
      }
    }, 1500);
  }, [currentUser, formData, saving, existingProfile]);

  useEffect(() => {
    autoSave();
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData, autoSave]);

  const updateFormData = <K extends keyof TaskerMetaForm>(
    section: K,
    data: Partial<TaskerMetaForm[K]> | TaskerMetaForm[K]
  ) => {
    setFormData((prev) => {
      const currentValue = prev[section];
      
      if (Array.isArray(currentValue) && Array.isArray(data)) {
        return { ...prev, [section]: data };
      }
      
      if (typeof currentValue === 'object' && currentValue !== null && !Array.isArray(currentValue)) {
        return { ...prev, [section]: { ...currentValue, ...data } };
      }
      
      return { ...prev, [section]: data };
    });
  };

  const handleComplete = async () => {
    if (!currentUser) return;
    
    try {
      setSaving(true);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/82659649-bb47-4cfa-8853-c0aec6c59272',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskerMeta.tsx:393',message:'handleComplete started',data:{userId:currentUser.uid,hasExistingProfile:!!existingProfile},timestamp:Date.now(),sessionId:'debug-session',runId:'initial_debug',hypothesisId:'A,B,C'})}).catch(()=>{});
      // #endregion
      // Load existing profile first - if it fails, continue without it (will create new profile)
      let currentProfile = existingProfile;
      if (!currentProfile) {
        try {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/82659649-bb47-4cfa-8853-c0aec6c59272',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskerMeta.tsx:396',message:'Before getTaskerProfileByUserId',data:{userId:currentUser.uid},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
          // #endregion
          currentProfile = await getTaskerProfileByUserId(currentUser.uid);
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/82659649-bb47-4cfa-8853-c0aec6c59272',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskerMeta.tsx:399',message:'After getTaskerProfileByUserId',data:{profileLoaded:!!currentProfile,profileId:currentProfile?.id},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
          // #endregion
        } catch (loadErr: any) {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/82659649-bb47-4cfa-8853-c0aec6c59272',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskerMeta.tsx:402',message:'Error loading profile - continuing without it',data:{error:loadErr?.message,errorCode:loadErr?.code,willCreateNew:true},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'A'})}).catch(()=>{});
          // #endregion
          // Don't throw - continue without existing profile (will create new one)
          console.warn('Could not load existing profile, will create new one:', loadErr);
          currentProfile = undefined;
        }
      }
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/82659649-bb47-4cfa-8853-c0aec6c59272',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskerMeta.tsx:407',message:'Before saveTaskerProfile',data:{userId:currentUser.uid,userEmail:currentUser.email,formDataKeys:Object.keys(formData),completionPercentage:formData.personalInfo?.firstName?1:0},timestamp:Date.now(),sessionId:'debug-session',runId:'initial_debug',hypothesisId:'B,C,D'})}).catch(()=>{});
      // #endregion
      const profileId = await saveTaskerProfile(
        currentUser.uid, 
        currentUser.email || '', 
        formData,
        currentProfile
      );
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/82659649-bb47-4cfa-8853-c0aec6c59272',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskerMeta.tsx:415',message:'After saveTaskerProfile',data:{profileId,success:true},timestamp:Date.now(),sessionId:'debug-session',runId:'initial_debug',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      
      // Metrics are recalculated locally — no cloud function needed
      
      // Dispatch event to notify other components that profile was updated
      window.dispatchEvent(new CustomEvent('profileUpdated'));
      
      // Redirect to public Tasker profile page
      navigate(`/tasker/${currentUser.uid}`);
    } catch (err: any) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/82659649-bb47-4cfa-8853-c0aec6c59272',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TaskerMeta.tsx:420',message:'Error caught in handleComplete',data:{errorMessage:err?.message,errorCode:err?.code,errorName:err?.name,stack:err?.stack?.substring(0,500),userId:currentUser?.uid},timestamp:Date.now(),sessionId:'debug-session',runId:'initial_debug',hypothesisId:'A,B,C,D,E'})}).catch(()=>{});
      // #endregion
      console.error('Error saving profile:', err);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const calculateProgress = () => {
    let completed = 0;
    let total = 0;

    // Personal
    total += 3;
    if (formData.personalInfo.firstName) completed++;
    if (formData.personalInfo.lastName) completed++;
    if (formData.personalInfo.displayName) completed++;

    // Professional
    total += 2;
    if (formData.professionalInfo.title) completed++;
    if (formData.professionalInfo.yearsOfExperience > 0) completed++;

    // Skills
    total += 1;
    if (formData.skills.primary.length > 0) completed++;

    // Preferences
    total += 2;
    if (formData.workPreferences.availability) completed++;
    if (formData.workPreferences.location) completed++;

    // Specialties
    total += 1;
    if (formData.specialties.length > 0) completed++;

    // AI showcase (core differentiator)
    total += 2;
    const ai = formData.aiBuilderShowcase;
    if (ai.publicTasksStory && ai.publicTasksStory.trim().length > 40) completed++;
    if (ai.aiToolExperiences.some((t) => t.toolName.trim() && t.story.trim().length > 15)) completed++;

    return Math.round((completed / total) * 100);
  };

  const isSectionComplete = (sectionId: string): boolean => {
    switch (sectionId) {
      case 'personal':
        return !!(formData.personalInfo.firstName && formData.personalInfo.lastName && formData.personalInfo.displayName);
      case 'ai_showcase': {
        const ai = formData.aiBuilderShowcase;
        return (
          (ai.publicTasksStory?.trim().length ?? 0) > 40 ||
          ai.aiToolExperiences.some((t) => t.toolName.trim() && t.story.trim().length > 10) ||
          ai.featuredLinks.some((l) => l.url.trim().length > 0)
        );
      }
      case 'professional':
        return !!(formData.professionalInfo.title && formData.professionalInfo.yearsOfExperience > 0);
      case 'skills':
        return formData.skills.primary.length > 0;
      case 'preferences':
        return !!(formData.workPreferences.availability && formData.workPreferences.location);
      case 'specialties':
        return formData.specialties.length > 0;
      case 'portfolio':
        return true; // Optional
      default:
        return false;
    }
  };

  const scrollToSection = useCallback((id: string) => {
    document.getElementById(`profile-section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (loading) return;
    const cleanups: Array<() => void> = [];
    SECTIONS.forEach((s) => {
      const el = document.getElementById(`profile-section-${s.id}`);
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setVisibleSectionId(s.id);
          });
        },
        { root: null, rootMargin: '-10% 0px -48% 0px', threshold: [0, 0.15] }
      );
      obs.observe(el);
      cleanups.push(() => obs.disconnect());
    });
    return () => cleanups.forEach((fn) => fn());
  }, [loading]);

  // File upload handler - must be defined before render functions and return statement
  const handleFileUpload = async (file: File, projectIndex: number, fileType: 'image' | 'document') => {
    if (!currentUser) {
      return;
    }

    const uploadKey = `project-${projectIndex}-${fileType}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const projectId = projectIndex.toString();
      const downloadURL = await uploadProjectImage(currentUser.uid, file, projectId);

      const projects = [...formData.portfolio.projects];
      if (fileType === 'image') {
        projects[projectIndex] = {
          ...projects[projectIndex],
          images: [...(projects[projectIndex].images || []), downloadURL],
        };
      }

      updateFormData('portfolio', { projects });
    } catch (error: any) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-purple-200">Loading your profile...</p>
        </div>
      </div>
    );
  }

  const progress = calculateProgress();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950 text-white relative overflow-hidden">
      {/* Layered atmosphere */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.18] bg-[radial-gradient(ellipse_90%_60%_at_50%_-15%,rgba(139,92,246,0.45),transparent)]" />
      <div className="absolute inset-0 opacity-25">
        <div className="absolute top-0 left-0 w-[28rem] h-[28rem] bg-violet-600 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-0 right-0 w-[26rem] h-[26rem] bg-cyan-600 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8 md:py-14 pb-32 lg:pb-16">
        {/* Hero */}
        <header className="mb-10 md:mb-14 text-center max-w-3xl mx-auto">
          <p className="text-[11px] font-semibold tracking-[0.35em] text-cyan-400/90 uppercase mb-4">Profile studio</p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-5 bg-gradient-to-r from-cyan-200 via-fuchsia-200 to-violet-300 bg-clip-text text-transparent">
            Your AI-native profile
          </h1>
          <p className="text-purple-200/75 text-base md:text-lg leading-relaxed">
            One continuous canvas — scroll, fill, and refine. Auto-save keeps every block safe while you work.
          </p>

          {/* Strength meter */}
          <div className="mt-10 max-w-lg mx-auto">
            <div className="flex justify-between items-end gap-4 mb-2">
              <span className="text-sm text-purple-200/85 font-medium">Profile strength</span>
              <span className="text-3xl font-bold tabular-nums bg-gradient-to-r from-violet-300 to-cyan-300 bg-clip-text text-transparent">
                {progress}%
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-white/[0.08] overflow-hidden ring-1 ring-white/10 shadow-inner">
              <div
                className="h-full rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-cyan-400 transition-[width] duration-500 ease-out shadow-[0_0_20px_rgba(34,211,238,0.35)]"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 mt-3 text-xs text-purple-300/55">
              {lastSaved && (
                <span className="inline-flex items-center gap-1.5">
                  <Save className="w-3.5 h-3.5 text-emerald-400/80" />
                  Saved {lastSaved.toLocaleTimeString()}
                </span>
              )}
              {saving && (
                <span className="inline-flex items-center gap-1.5 text-cyan-300/85">
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                  Saving…
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Mobile: jump nav */}
        <div className="lg:hidden sticky top-16 z-30 -mx-4 px-4 py-3 mb-8 bg-slate-950/90 backdrop-blur-xl border-y border-white/10 shadow-lg shadow-black/20">
          <div
            className="flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {SECTIONS.map((section, i) => {
              const done = isSectionComplete(section.id);
              const active = visibleSectionId === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => scrollToSection(section.id)}
                  className={`snap-start shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs sm:text-sm font-semibold border transition-all ${
                    active
                      ? 'border-cyan-400/70 bg-cyan-500/15 text-white shadow-[0_0_20px_rgba(34,211,238,0.15)]'
                      : 'border-white/10 bg-white/[0.04] text-purple-200/85 hover:border-white/20'
                  }`}
                >
                  <span className="font-mono text-[10px] opacity-60">{String(i + 1).padStart(2, '0')}</span>
                  {section.shortTitle}
                  {done && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" aria-hidden />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-10 lg:gap-14 items-start">
          {/* All sections — single scroll */}
          <div className="space-y-12 md:space-y-16 min-w-0">
            {SECTIONS.map((section, index) => {
              const Icon = section.icon;
              const done = isSectionComplete(section.id);
              return (
                <section
                  key={section.id}
                  id={`profile-section-${section.id}`}
                  className="scroll-mt-28 md:scroll-mt-36"
                >
                  <div className="relative rounded-[1.75rem] border border-white/10 bg-gradient-to-b from-white/[0.08] to-white/[0.02] backdrop-blur-2xl overflow-hidden shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_28px_100px_-24px_rgba(76,29,149,0.55)]">
                    <div className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r ${section.color} opacity-90`} />
                    <div
                      className={`absolute -top-28 -right-20 w-56 h-56 rounded-full bg-gradient-to-br ${section.color} opacity-[0.14] blur-3xl pointer-events-none`}
                    />

                    <div className="relative p-6 md:p-10 lg:p-12">
                      <div className="flex flex-col lg:flex-row lg:items-stretch gap-8 lg:gap-12">
                        <div className="flex lg:flex-col items-center lg:items-start gap-4 lg:w-36 shrink-0">
                          <div
                            className={`flex h-14 w-14 lg:h-[4.5rem] lg:w-[4.5rem] items-center justify-center rounded-2xl bg-gradient-to-br ${section.color} shadow-xl ring-2 ring-white/15`}
                          >
                            <Icon className="w-7 h-7 lg:w-9 lg:h-9 text-white" aria-hidden />
                          </div>
                          <div className="hidden lg:flex flex-col items-center gap-2 pt-2 border-t border-white/10 w-full">
                            <span className="text-[10px] font-mono tracking-[0.2em] text-purple-400/80">STEP</span>
                            <span className="text-2xl font-bold text-white tabular-nums">{String(index + 1).padStart(2, '0')}</span>
                            {done ? (
                              <span className="text-[10px] text-emerald-400 font-medium">Done</span>
                            ) : (
                              <span className="text-[10px] text-purple-400/60 font-medium">Open</span>
                            )}
                          </div>
                        </div>

                        <div className="flex-1 min-w-0 space-y-6">
                          <div>
                            <div className="flex flex-wrap items-center gap-3 mb-2">
                              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-white">{section.title}</h2>
                              <span className="lg:hidden text-xs font-mono px-2.5 py-1 rounded-lg bg-white/10 text-purple-100/90 border border-white/10">
                                {String(index + 1).padStart(2, '0')}
                              </span>
                            </div>
                            <p className="text-purple-200/75 text-sm md:text-base">{section.subtitle}</p>
                            <p className="text-purple-100/90 mt-4 text-[15px] md:text-base leading-relaxed border-l-2 border-cyan-400/50 pl-4 md:pl-5 bg-gradient-to-r from-cyan-500/5 to-transparent py-1 rounded-r-lg">
                              {section.prompt}
                            </p>
                          </div>
                          <div className="space-y-6 pt-1">{renderSectionContent(section.id)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              );
            })}
          </div>

          {/* Desktop sticky rail */}
          <aside className="hidden lg:block">
            <div className="sticky top-32 space-y-6">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl overflow-hidden shadow-xl">
                <div className="px-5 py-4 border-b border-white/10 bg-white/[0.03]">
                  <p className="text-[10px] font-semibold tracking-[0.25em] text-purple-300/75 uppercase">On this page</p>
                  <p className="text-sm text-white mt-1 font-medium">Jump to any section</p>
                </div>
                <nav className="p-2 max-h-[min(58vh,480px)] overflow-y-auto space-y-0.5">
                  {SECTIONS.map((section, i) => {
                    const done = isSectionComplete(section.id);
                    const active = visibleSectionId === section.id;
                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left flex items-start gap-3 rounded-xl px-3 py-2.5 transition-all ${
                          active
                            ? 'bg-gradient-to-r from-cyan-500/20 to-transparent ring-1 ring-cyan-400/35 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]'
                            : 'hover:bg-white/[0.04]'
                        }`}
                      >
                        <span
                          className={`text-[11px] font-mono mt-0.5 tabular-nums ${done ? 'text-emerald-400' : 'text-purple-500'}`}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-white/95 leading-snug">{section.title}</span>
                          <span className="block text-[11px] text-purple-400/65 mt-0.5">{section.shortTitle}</span>
                        </span>
                        {done && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" aria-hidden />}
                      </button>
                    );
                  })}
                </nav>
              </div>

              <div className="rounded-2xl border border-emerald-400/25 bg-gradient-to-br from-emerald-500/[0.12] to-cyan-500/[0.06] p-5 shadow-lg">
                <p className="text-sm text-emerald-100 font-semibold">Go live</p>
                <p className="text-xs text-purple-200/65 mt-1 mb-4 leading-relaxed">
                  Save everything and open your public profile. You can keep editing anytime.
                </p>
                <button
                  type="button"
                  onClick={handleComplete}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-slate-950 bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 hover:brightness-105 transition-all disabled:opacity-50 shadow-lg shadow-emerald-500/15"
                >
                  {saving ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Complete profile
                    </>
                  )}
                </button>
              </div>
            </div>
          </aside>
        </div>

        {/* Mobile dock */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-slate-950 via-slate-950/98 to-transparent border-t border-white/10 backdrop-blur-md">
          <div className="flex items-center gap-3 max-w-lg mx-auto">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-purple-300/70 font-medium uppercase tracking-wide">Strength</p>
              <div className="h-2 mt-1 rounded-full bg-white/10 overflow-hidden ring-1 ring-white/5">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-[width] duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleComplete}
              disabled={saving}
              className="shrink-0 px-5 py-3 rounded-xl font-bold text-slate-950 bg-gradient-to-r from-emerald-400 to-cyan-400 disabled:opacity-45 text-sm shadow-lg"
            >
              {saving ? <Loader className="w-5 h-5 animate-spin" /> : 'Done'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  function renderSectionContent(sectionId: string) {
    switch (sectionId) {
      case 'personal':
        return renderPersonalSection();
      case 'ai_showcase':
        return renderAiShowcaseSection();
      case 'professional':
        return renderProfessionalSection();
      case 'skills':
        return renderSkillsSection();
      case 'preferences':
        return renderPreferencesSection();
      case 'specialties':
        return renderSpecialtiesSection();
      case 'portfolio':
        return renderPortfolioSection();
      default:
        return null;
    }
  }

  function renderAiShowcaseSection() {
    const ai = formData.aiBuilderShowcase;

    const setAi = (patch: Partial<typeof ai>) => {
      setFormData((prev) => ({
        ...prev,
        aiBuilderShowcase: { ...prev.aiBuilderShowcase, ...patch },
      }));
    };

    const addToolRow = () => {
      setAi({
        aiToolExperiences: [...ai.aiToolExperiences, { toolName: '', story: '' }],
      });
    };

    const updateToolRow = (index: number, field: 'toolName' | 'story', value: string) => {
      let next = [...ai.aiToolExperiences];
      if (next.length === 0) next = [{ toolName: '', story: '' }];
      next[index] = { ...next[index], [field]: value };
      setAi({ aiToolExperiences: next });
    };

    const removeToolRow = (index: number) => {
      setAi({ aiToolExperiences: ai.aiToolExperiences.filter((_, i) => i !== index) });
    };

    const addFeaturedLink = () => {
      setAi({ featuredLinks: [...ai.featuredLinks, { url: '' }] });
    };

    const updateFeaturedUrl = (index: number, url: string) => {
      const next = [...ai.featuredLinks];
      next[index] = { ...next[index], url };
      setAi({ featuredLinks: next });
    };

    const removeFeaturedLink = (index: number) => {
      setAi({ featuredLinks: ai.featuredLinks.filter((_, i) => i !== index) });
    };

    const fetchPreviewFor = async (index: number) => {
      const link = ai.featuredLinks[index];
      if (!link?.url?.trim()) return;
      setFetchingLinkIdx(index);
      try {
        const meta = await fetchLinkPreview(link.url);
        if (!meta) return;
        const next = [...ai.featuredLinks];
        next[index] = {
          url: meta.url,
          title: meta.title,
          description: meta.description,
          imageUrl: meta.imageUrl,
          siteName: meta.siteName,
        };
        setAi({ featuredLinks: next });
      } finally {
        setFetchingLinkIdx(null);
      }
    };

    return (
      <div className="space-y-8 text-left">
        <div className="rounded-2xl border border-cyan-500/25 bg-cyan-500/5 p-4 text-sm text-cyan-100/90">
          <p className="font-semibold text-cyan-200 mb-1">Why this section matters</p>
          <p className="text-cyan-100/80 leading-relaxed">
            Instagram shows lifestyle; Reddit shows threads. <strong>Here you prove execution</strong> — tasks you
            define, AI tools you actually use (e.g. ElevenLabs, Cursor, Claude), and links that back it up. That’s
            your portable reputation for collaborators and clients.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2 text-purple-200">
            Tasks you create & what they’re about
          </label>
          <p className="text-xs text-purple-300/70 mb-2">
            Describe the kinds of AI-backed work you ship — not a job title, but outcomes and themes.
          </p>
          <textarea
            value={ai.publicTasksStory || ''}
            onChange={(e) => setAi({ publicTasksStory: e.target.value })}
            onBlur={autoSave}
            rows={5}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 text-white placeholder-purple-400/50 resize-none"
            placeholder="e.g. I spec and ship internal copilots, voice agents with ElevenLabs + custom RAG, and evaluation harnesses for LLM features…"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-purple-200">Experience with AI applications</label>
            <button
              type="button"
              onClick={() => {
                addToolRow();
                void autoSave();
              }}
              className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10"
            >
              <Plus className="w-3.5 h-3.5" /> Add tool
            </button>
          </div>
          <p className="text-xs text-purple-300/70 mb-3">
            Name the app (e.g. ElevenLabs, Cursor, ComfyUI) and what you’ve done with it — shipped, experimented, or
            productionized.
          </p>
          <div className="space-y-4">
            {(ai.aiToolExperiences.length === 0 ? [{ toolName: '', story: '' }] : ai.aiToolExperiences).map((row, idx) => (
              <div key={idx} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={row.toolName}
                    onChange={(e) => updateToolRow(idx, 'toolName', e.target.value)}
                    onBlur={autoSave}
                    className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                    placeholder="Tool name (e.g. ElevenLabs)"
                  />
                  {(ai.aiToolExperiences.length > 1 || (ai.aiToolExperiences.length === 1 && (ai.aiToolExperiences[0].toolName || ai.aiToolExperiences[0].story))) && (
                    <button
                      type="button"
                      onClick={() => {
                        removeToolRow(idx);
                        void autoSave();
                      }}
                      className="p-2 text-red-300 hover:bg-red-500/10 rounded-lg"
                      aria-label="Remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <textarea
                  value={row.story}
                  onChange={(e) => updateToolRow(idx, 'story', e.target.value)}
                  onBlur={autoSave}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm resize-none"
                  placeholder="What you built, learned, or shipped with this tool…"
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="w-4 h-4 text-fuchsia-300" />
            <label className="text-sm font-medium text-purple-200">Featured links (rich preview)</label>
          </div>
          <p className="text-xs text-purple-300/70 mb-3">
            Paste any URL — we fetch a cover image & title when possible (Open Graph). Great for demos, articles, or
            product pages.
          </p>
          <div className="space-y-6">
            {ai.featuredLinks.map((link, idx) => (
              <div key={idx} className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => updateFeaturedUrl(idx, e.target.value)}
                    onBlur={autoSave}
                    className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                    placeholder="https://…"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={fetchingLinkIdx === idx || !link.url?.trim()}
                      onClick={() => void fetchPreviewFor(idx)}
                      className="px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-600 to-cyan-600 text-white text-sm font-medium disabled:opacity-50"
                    >
                      {fetchingLinkIdx === idx ? 'Fetching…' : 'Load preview'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        removeFeaturedLink(idx);
                        void autoSave();
                      }}
                      className="p-2 text-red-300 hover:bg-red-500/10 rounded-lg"
                      aria-label="Remove link"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {link.url.trim() && (link.title || link.imageUrl) && (
                  <LinkPreviewCard
                    url={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                    title={link.title}
                    description={link.description}
                    imageUrl={link.imageUrl}
                    siteName={link.siteName}
                    variant="purple"
                  />
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                addFeaturedLink();
                void autoSave();
              }}
              className="w-full py-3 rounded-xl border border-dashed border-white/20 text-purple-200 hover:bg-white/5 text-sm"
            >
              + Add another link
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderPersonalSection() {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-purple-200">First Name *</label>
            <input
              type="text"
              value={formData.personalInfo?.firstName || ''}
              onChange={(e) => updateFormData('personalInfo', { firstName: e.target.value })}
              onBlur={autoSave}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 text-white transition-all"
              placeholder="John"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-purple-200">Last Name *</label>
            <input
              type="text"
              value={formData.personalInfo?.lastName || ''}
              onChange={(e) => updateFormData('personalInfo', { lastName: e.target.value })}
              onBlur={autoSave}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 text-white transition-all"
              placeholder="Doe"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-purple-200">Display Name *</label>
          <input
            type="text"
            value={formData.personalInfo?.displayName || ''}
            onChange={(e) => updateFormData('personalInfo', { displayName: e.target.value })}
            onBlur={autoSave}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 text-white transition-all"
            placeholder="john_doe"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-purple-200">Location</label>
            <input
              type="text"
              value={formData.personalInfo?.location || ''}
              onChange={(e) => updateFormData('personalInfo', { location: e.target.value })}
              onBlur={autoSave}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 text-white transition-all"
              placeholder="City, Country"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-purple-200">Timezone</label>
            <select
              value={formData.personalInfo.timezone}
              onChange={(e) => updateFormData('personalInfo', { timezone: e.target.value })}
              onBlur={autoSave}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 text-white transition-all"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz} className="bg-slate-900">
                  {tz}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-purple-200">Bio</label>
          <textarea
            value={formData.personalInfo?.bio || ''}
            onChange={(e) => updateFormData('personalInfo', { bio: e.target.value })}
            onBlur={autoSave}
            rows={4}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 text-white resize-none transition-all"
            placeholder="Tell us about yourself..."
          />
        </div>
      </div>
    );
  }

  function renderProfessionalSection() {
    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2 text-purple-200">Professional Title *</label>
          <input
            type="text"
            value={formData.professionalInfo?.title || ''}
            onChange={(e) => updateFormData('professionalInfo', { title: e.target.value })}
            onBlur={autoSave}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 text-white transition-all"
            placeholder="e.g., Senior DevOps Engineer"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-3 text-purple-200">Experience Level *</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {EXPERIENCE_LEVELS.map((level) => (
              <button
                key={level.value}
                type="button"
                onClick={() => updateFormData('professionalInfo', { experienceLevel: level.value })}
                onBlur={autoSave}
                className={`p-4 rounded-xl border transition-all text-left ${
                  formData.professionalInfo.experienceLevel === level.value
                    ? 'bg-blue-500/20 border-blue-400 shadow-lg scale-105'
                    : 'bg-white/5 border-white/10 hover:border-blue-400/50'
                }`}
              >
                <div className="text-2xl mb-1">{level.emoji}</div>
                <div className="font-medium text-sm">{level.label}</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-purple-200">Years of Experience *</label>
          <input
            type="number"
            min="0"
            max="50"
            value={formData.professionalInfo?.yearsOfExperience || ''}
            onChange={(e) => updateFormData('professionalInfo', { yearsOfExperience: parseInt(e.target.value) || 0 })}
            onBlur={autoSave}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 text-white transition-all"
            placeholder="5"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-purple-200">LinkedIn</label>
            <input
              type="url"
              value={formData.professionalInfo?.linkedInUrl || ''}
              onChange={(e) => updateFormData('professionalInfo', { linkedInUrl: e.target.value })}
              onBlur={autoSave}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 text-white transition-all"
              placeholder="https://linkedin.com/in/..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-purple-200">Portfolio</label>
            <input
              type="url"
              value={formData.professionalInfo?.portfolioUrl || ''}
              onChange={(e) => updateFormData('professionalInfo', { portfolioUrl: e.target.value })}
              onBlur={autoSave}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 text-white transition-all"
              placeholder="https://yourportfolio.com"
            />
          </div>
        </div>
      </div>
    );
  }

  function renderSkillsSection() {
    const addSkill = () => {
      if (!tempSkill.trim()) return;
      const currentSkills = formData.skills[skillType];
      if (currentSkills.includes(tempSkill.trim())) return;
      
      updateFormData('skills', {
        [skillType]: [...currentSkills, tempSkill.trim()],
      });
      setTempSkill('');
    };

    return (
      <div className="space-y-6">
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setSkillType('primary')}
            className={`flex-1 px-4 py-2 rounded-xl transition-all ${
              skillType === 'primary'
                ? 'bg-amber-500/20 border-2 border-amber-400'
                : 'bg-white/5 border border-white/10'
            }`}
          >
            Primary Skills
          </button>
          <button
            type="button"
            onClick={() => setSkillType('secondary')}
            className={`flex-1 px-4 py-2 rounded-xl transition-all ${
              skillType === 'secondary'
                ? 'bg-amber-500/20 border-2 border-amber-400'
                : 'bg-white/5 border border-white/10'
            }`}
          >
            Secondary Skills
          </button>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={tempSkill}
            onChange={(e) => setTempSkill(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addSkill();
              }
            }}
            className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/20 text-white transition-all"
            placeholder={`Add ${skillType === 'primary' ? 'primary' : 'secondary'} skill...`}
          />
          <button
            type="button"
            onClick={addSkill}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.skills[skillType].map((skill) => (
            <span
              key={skill}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-400/30 text-sm"
            >
              {skill}
              <button
                type="button"
                onClick={() => {
                  updateFormData('skills', {
                    [skillType]: formData.skills[skillType].filter((s) => s !== skill),
                  });
                }}
                className="hover:text-red-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </span>
          ))}
        </div>
      </div>
    );
  }

  function renderPreferencesSection() {
    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-3 text-purple-200">Availability *</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {AVAILABILITY_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => updateFormData('workPreferences', { availability: option.value })}
                onBlur={autoSave}
                className={`p-4 rounded-xl border transition-all ${
                  formData.workPreferences.availability === option.value
                    ? 'bg-emerald-500/20 border-emerald-400 shadow-lg scale-105'
                    : 'bg-white/5 border-white/10 hover:border-emerald-400/50'
                }`}
              >
                <div className="text-2xl mb-1">{option.emoji}</div>
                <div className="font-medium text-sm">{option.label}</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-3 text-purple-200">Location Preference *</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {LOCATION_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => updateFormData('workPreferences', { location: option.value })}
                onBlur={autoSave}
                className={`p-4 rounded-xl border transition-all ${
                  formData.workPreferences.location === option.value
                    ? 'bg-emerald-500/20 border-emerald-400 shadow-lg scale-105'
                    : 'bg-white/5 border-white/10 hover:border-emerald-400/50'
                }`}
              >
                <div className="text-2xl mb-1">{option.emoji}</div>
                <div className="font-medium text-sm">{option.label}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-purple-200">Hourly Rate</label>
            <div className="flex gap-2">
              <select
                value={formData.workPreferences.currency}
                onChange={(e) => updateFormData('workPreferences', { currency: e.target.value })}
                onBlur={autoSave}
                className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-emerald-400 focus:outline-none text-white"
              >
                <option value="USD" className="bg-slate-900">USD</option>
                <option value="EUR" className="bg-slate-900">EUR</option>
                <option value="GBP" className="bg-slate-900">GBP</option>
              </select>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.workPreferences.hourlyRate || ''}
                onChange={(e) => updateFormData('workPreferences', { hourlyRate: parseFloat(e.target.value) || 0 })}
                onBlur={autoSave}
                className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/20 text-white transition-all"
                placeholder="50"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-purple-200">Hours Per Week</label>
            <input
              type="number"
              min="1"
              max="80"
              value={formData.workPreferences.preferredHoursPerWeek || ''}
              onChange={(e) => updateFormData('workPreferences', { preferredHoursPerWeek: parseInt(e.target.value) || undefined })}
              onBlur={autoSave}
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/20 text-white transition-all"
              placeholder="20"
            />
          </div>
        </div>
      </div>
    );
  }

  function renderSpecialtiesSection() {
    const addSpecialty = () => {
      if (!tempSpecialty.trim()) return;
      if (formData.specialties.includes(tempSpecialty.trim())) return;
      
      updateFormData('specialties', [...formData.specialties, tempSpecialty.trim()]);
      setTempSpecialty('');
    };

    return (
      <div className="space-y-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={tempSpecialty}
            onChange={(e) => setTempSpecialty(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addSpecialty();
              }
            }}
            className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-pink-400 focus:outline-none focus:ring-2 focus:ring-pink-400/20 text-white transition-all"
            placeholder="Add a specialty..."
          />
          <button
            type="button"
            onClick={addSpecialty}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.specialties.map((specialty) => (
            <span
              key={specialty}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/20 border border-pink-400/30 text-sm"
            >
              <Sparkle className="w-4 h-4" />
              {specialty}
              <button
                type="button"
                onClick={() => {
                  updateFormData('specialties', formData.specialties.filter((s) => s !== specialty));
                }}
                className="hover:text-red-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </span>
          ))}
        </div>
      </div>
    );
  }

  function renderPortfolioSection() {
    return (
      <div className="space-y-8">
        {/* Projects Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold mb-1">Your Projects</h3>
              <p className="text-sm text-purple-200/70">Showcase your best work</p>
            </div>
            <button
              type="button"
              onClick={() => {
                const projects = [...formData.portfolio.projects];
                projects.push({
                  title: '',
                  description: '',
                  category: '',
                  technologies: [],
                  duration: '',
                  role: '',
                  outcome: '',
                  url: '',
                  images: [],
                  completedAt: new Date(),
                });
                updateFormData('portfolio', { projects });
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:shadow-lg transition-all"
            >
              <Plus className="w-5 h-5" />
              <span>Add Project</span>
            </button>
          </div>

          <div className="space-y-6">
            {formData.portfolio.projects.map((project, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-white/5 border border-white/10 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Project #{index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => {
                      const projects = formData.portfolio.projects.filter((_, i) => i !== index);
                      updateFormData('portfolio', { projects });
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Project Title *
                    </label>
                    <input
                      type="text"
                      value={project.title}
                      onChange={(e) => {
                        const projects = [...formData.portfolio.projects];
                        projects[index] = { ...projects[index], title: e.target.value };
                        updateFormData('portfolio', { projects });
                      }}
                      onBlur={autoSave}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/20 text-white transition-all"
                      placeholder="e.g., E-commerce Platform Redesign"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Category
                    </label>
                    <input
                      type="text"
                      value={project.category}
                      onChange={(e) => {
                        const projects = [...formData.portfolio.projects];
                        projects[index] = { ...projects[index], category: e.target.value };
                        updateFormData('portfolio', { projects });
                      }}
                      onBlur={autoSave}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/20 text-white transition-all"
                      placeholder="e.g., Web Development"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Duration
                    </label>
                    <input
                      type="text"
                      value={project.duration}
                      onChange={(e) => {
                        const projects = [...formData.portfolio.projects];
                        projects[index] = { ...projects[index], duration: e.target.value };
                        updateFormData('portfolio', { projects });
                      }}
                      onBlur={autoSave}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/20 text-white transition-all"
                      placeholder="e.g., 3 months"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Your Role
                    </label>
                    <input
                      type="text"
                      value={project.role}
                      onChange={(e) => {
                        const projects = [...formData.portfolio.projects];
                        projects[index] = { ...projects[index], role: e.target.value };
                        updateFormData('portfolio', { projects });
                      }}
                      onBlur={autoSave}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/20 text-white transition-all"
                      placeholder="e.g., Lead Developer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Project URL
                    </label>
                    <input
                      type="url"
                      value={project.url}
                      onChange={(e) => {
                        const projects = [...formData.portfolio.projects];
                        projects[index] = { ...projects[index], url: e.target.value };
                        updateFormData('portfolio', { projects });
                      }}
                      onBlur={autoSave}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/20 text-white transition-all"
                      placeholder="https://yourproject.com"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Technologies Used
                    </label>
                    <input
                      type="text"
                      value={project.technologies.join(', ')}
                      onChange={(e) => {
                        const projects = [...formData.portfolio.projects];
                        projects[index] = {
                          ...projects[index],
                          technologies: e.target.value.split(',').map(t => t.trim()).filter(Boolean),
                        };
                        updateFormData('portfolio', { projects });
                      }}
                      onBlur={autoSave}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/20 text-white transition-all"
                      placeholder="React, Node.js, MongoDB (comma separated)"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Description *
                    </label>
                    <textarea
                      value={project.description}
                      onChange={(e) => {
                        const projects = [...formData.portfolio.projects];
                        projects[index] = { ...projects[index], description: e.target.value };
                        updateFormData('portfolio', { projects });
                      }}
                      onBlur={autoSave}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/20 text-white resize-none transition-all"
                      placeholder="Describe the project, challenges, and your contributions..."
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Key Outcomes / Results
                    </label>
                    <textarea
                      value={project.outcome || ''}
                      onChange={(e) => {
                        const projects = [...formData.portfolio.projects];
                        projects[index] = { ...projects[index], outcome: e.target.value };
                        updateFormData('portfolio', { projects });
                      }}
                      onBlur={autoSave}
                      rows={2}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-400/20 text-white resize-none transition-all"
                      placeholder="e.g., Increased user engagement by 40%, reduced load time by 50%"
                    />
                  </div>

                  {/* File Upload Section */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Project Images / Screenshots
                    </label>
                    <div className="flex flex-wrap gap-3 mb-3">
                      {project.images?.map((imageUrl, imgIndex) => (
                        <div key={imgIndex} className="relative group">
                          <img
                            src={imageUrl}
                            alt={`Project ${index + 1} screenshot ${imgIndex + 1}`}
                            className="w-24 h-24 object-cover rounded-lg border border-white/10"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const projects = [...formData.portfolio.projects];
                              projects[index] = {
                                ...projects[index],
                                images: projects[index].images?.filter((_, i) => i !== imgIndex) || [],
                              };
                              updateFormData('portfolio', { projects });
                            }}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-violet-400/50 transition-colors bg-white/5">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {uploadingFiles[`project-${index}-image`] ? (
                          <Loader className="w-8 h-8 text-violet-400 animate-spin mb-2" />
                        ) : (
                          <Upload className="w-8 h-8 text-violet-400 mb-2" />
                        )}
                        <p className="mb-2 text-sm text-purple-200">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-purple-200/70">PNG, JPG, GIF (MAX. 5MB)</p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              alert('File size must be less than 5MB');
                              return;
                            }
                            handleFileUpload(file, index, 'image');
                          }
                        }}
                        disabled={uploadingFiles[`project-${index}-image`]}
                      />
                    </label>
                  </div>
                </div>
              </div>
            ))}

            {formData.portfolio.projects.length === 0 && (
              <div className="text-center py-12 border-2 border-dashed border-white/20 rounded-2xl">
                <FolderOpen className="w-16 h-16 text-violet-400 mx-auto mb-4 opacity-50" />
                <p className="text-purple-200/70 mb-4">No projects added yet</p>
                <button
                  type="button"
                  onClick={() => {
                    updateFormData('portfolio', {
                      projects: [{
                        title: '',
                        description: '',
                        category: '',
                        technologies: [],
                        duration: '',
                        role: '',
                        outcome: '',
                        url: '',
                        images: [],
                        completedAt: new Date(),
                      }],
                    });
                  }}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:shadow-lg transition-all"
                >
                  Add Your First Project
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Achievements Section */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold mb-1">Key Achievements</h3>
              <p className="text-sm text-purple-200/70">Highlight your major accomplishments</p>
            </div>
            <button
              type="button"
              onClick={() => {
                const achievements = [...formData.portfolio.achievements];
                achievements.push({
                  title: '',
                  description: '',
                  category: '',
                  date: new Date(),
                  metrics: '',
                  proofUrl: '',
                });
                updateFormData('portfolio', { achievements });
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 hover:shadow-lg transition-all"
            >
              <Plus className="w-5 h-5" />
              <span>Add Achievement</span>
            </button>
          </div>

          <div className="space-y-4">
            {formData.portfolio.achievements.map((achievement, index) => (
              <div
                key={index}
                className="p-5 rounded-2xl bg-white/5 border border-white/10 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">Achievement #{index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => {
                      const achievements = formData.portfolio.achievements.filter((_, i) => i !== index);
                      updateFormData('portfolio', { achievements });
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Achievement Title *
                    </label>
                    <input
                      type="text"
                      value={achievement.title}
                      onChange={(e) => {
                        const achievements = [...formData.portfolio.achievements];
                        achievements[index] = { ...achievements[index], title: e.target.value };
                        updateFormData('portfolio', { achievements });
                      }}
                      onBlur={autoSave}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/20 text-white transition-all"
                      placeholder="e.g., Reduced System Downtime by 90%"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Category
                    </label>
                    <input
                      type="text"
                      value={achievement.category}
                      onChange={(e) => {
                        const achievements = [...formData.portfolio.achievements];
                        achievements[index] = { ...achievements[index], category: e.target.value };
                        updateFormData('portfolio', { achievements });
                      }}
                      onBlur={autoSave}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/20 text-white transition-all"
                      placeholder="e.g., Performance"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Date
                    </label>
                    <input
                      type="date"
                      value={(() => {
                        if (!achievement.date) return '';
                        try {
                          const date = achievement.date instanceof Date 
                            ? achievement.date 
                            : new Date(achievement.date);
                          if (isNaN(date.getTime())) return '';
                          return date.toISOString().split('T')[0];
                        } catch (e) {
                          return '';
                        }
                      })()}
                      onChange={(e) => {
                        const achievements = [...formData.portfolio.achievements];
                        achievements[index] = { ...achievements[index], date: new Date(e.target.value) };
                        updateFormData('portfolio', { achievements });
                      }}
                      onBlur={autoSave}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/20 text-white transition-all"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Description *
                    </label>
                    <textarea
                      value={achievement.description}
                      onChange={(e) => {
                        const achievements = [...formData.portfolio.achievements];
                        achievements[index] = { ...achievements[index], description: e.target.value };
                        updateFormData('portfolio', { achievements });
                      }}
                      onBlur={autoSave}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/20 text-white resize-none transition-all"
                      placeholder="Describe what you achieved and how..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Metrics / Results
                    </label>
                    <input
                      type="text"
                      value={achievement.metrics || ''}
                      onChange={(e) => {
                        const achievements = [...formData.portfolio.achievements];
                        achievements[index] = { ...achievements[index], metrics: e.target.value };
                        updateFormData('portfolio', { achievements });
                      }}
                      onBlur={autoSave}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/20 text-white transition-all"
                      placeholder="e.g., 90% improvement, $50K saved"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-200">
                      Proof URL (optional)
                    </label>
                    <input
                      type="url"
                      value={achievement.proofUrl || ''}
                      onChange={(e) => {
                        const achievements = [...formData.portfolio.achievements];
                        achievements[index] = { ...achievements[index], proofUrl: e.target.value };
                        updateFormData('portfolio', { achievements });
                      }}
                      onBlur={autoSave}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/20 text-white transition-all"
                      placeholder="Link to certificate, article, etc."
                    />
                  </div>
                </div>
              </div>
            ))}

            {formData.portfolio.achievements.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed border-white/20 rounded-2xl">
                <Trophy className="w-12 h-12 text-yellow-400 mx-auto mb-3 opacity-50" />
                <p className="text-purple-200/70 mb-4">No achievements added yet</p>
                <button
                  type="button"
                  onClick={() => {
                    updateFormData('portfolio', {
                      achievements: [{
                        title: '',
                        description: '',
                        category: '',
                        date: new Date(),
                        metrics: '',
                        proofUrl: '',
                      }],
                    });
                  }}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 hover:shadow-lg transition-all"
                >
                  Add Your First Achievement
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
};

export default TaskerMeta;
